import { Component, Input, OnInit } from '@angular/core';
import { HeaderService } from '../../../../services/header/header.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportConfig } from '../../../../models/ReportConfig';
import { ClientService } from '../../../../services/Client/client.service';
import { Client } from '../../../../models/client';
import { FournisseurService } from '../../../../services/Fournisseur/fournisseur.service';
import { Fournisseur } from '../../../../models/fournisseur';
import { ProductsService } from '../../../../services/products/products.service';
import { Product } from '../../../../models/Product';
import { PaginatedResponse } from '../../../../models/paginatedResponse';
import { ToastrService } from 'ngx-toastr';
import { DocumentService } from '../../../../services/document/document.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-detail-rapport',
  templateUrl: './detail-rapport.component.html',
  styleUrls: ['./detail-rapport.component.scss']
})
export class DetailRapportComponent implements OnInit {

    @Input() config!: ReportConfig;
    data: any[] = [];
    reportName: string = '';
    taux: number | null = null;
    readonly tauxStep = 1;

    private normalizedReportName = '';
    private lastReportQueryName = '';
    retenuesFournisseurBannerDismissed = false;

    stationId = 0;
    userId = 0;
    contactClients: Client[] = [];
    rapportFournisseurs: Fournisseur[] = [];
    isLoadingContacts = false;
    selectedContactId: number | null = null;
    selectedGroupById: string | null = null;
    selectedProductId: number | null = null;
    selectedDocumentType: string | null = null;

    readonly rapportGroupByOptions = [
        { id: 'opt1', label: 'Client 1' },
        { id: 'opt2', label: 'Client 2' },
    ];
    rapportProducts: Product[] = [];
    isLoadingProducts = false;
    isLoadingVenteParClientDocs = false;
    isLoadingAchatFournisseurDocs = false;
    /** Chargement des lignes « Détails ventes par ligne produit » (documents API) */
    isLoadingDetailsVentesParProduitDocs = false;
    /** Pagination « Détails ventes par ligne produit » (renvoyée par l'API) */
    detailsVentesParProduitTotal = 0;
    /** Chargement des lignes « Détails d'achat par ligne produit » (lignes API) */
    isLoadingDetailsAchatParProduitDocs = false;
    /** Pagination « Détails d'achat par ligne produit » (renvoyée par l'API) */
    detailsAchatParProduitTotal = 0;

    /** Pagination (même logique que shared.component) — rapport « Vente par client » uniquement */
    venteParClientCurrentPage = 1;
    venteParClientTotalPages = 1;
    venteParClientPages: number[] = [];
    venteParClientPageSize = 10;
    readonly venteParClientPageSizeOptions = [10, 25, 50, 100];

    /**
     * Types de documents de vente (aligné sur shared.component getTypeDocumentId) :
     * devis, commande client, bon sortie, bon de livraison, facture.
     */
    private readonly VENTE_DOCUMENT_TYPE_IDS = new Set<number>([7, 8, 9, 10, 11]);

    /** Commande fournisseur, bon réception, facture fournisseur */
    private readonly ACHAT_DOCUMENT_TYPE_IDS = new Set<number>([12, 13, 14]);

    readonly rapportDocumentOptions = [
        { id: 'BL', label: 'Bon de livraison' },
        { id: 'FA', label: 'Facture' },
    ];

    private readonly CONTACT_REPORTS = new Set<string>([
        'vente par client',
        'achat par fournisseur',
        'facture clients payees depuis plus d un mois',
        'chiffre d affaires par produit par client',
        'chiffre d affaire par produit par client',
        'details ventes par ligne produit',
        'details de ventes par ligne de produit',
        'details d achat par ligne produit',
        'details d achat par ligne de produit',
        'transactions fournisseurs',
        'transaction client',
        'transactions client',
        'transactions clients',
        'tva achats',
        'tva achat',
        'tva de vente',
        'tva ventes',
        'total des retenues pour les fournisseurs',
    ]);

    private readonly PRODUCT_REPORTS = new Set<string>([
        'details ventes par ligne produit',
        'details de ventes par ligne de produit',
        'details d achat par ligne produit',
        'details d achat par ligne de produit',
        'chiffre d affaires par produit',
        'chiffre d affaire par produit',
        'chiffre d affaires par produit par client',
        'chiffre d affaire par produit par client',
        'mouvement detaille de stock',
        'mouvement detaille de stcok',
    ]);

    private readonly GROUP_BY_REPORTS = new Set<string>([
        'chiffre d affaires par produit',
        'chiffre d affaire par produit',
        'chiffre d affaires par produit par client',
        'chiffre d affaire par produit par client',
    ]);

    private readonly DOCUMENT_REPORTS = new Set<string>([
        'benefice commercial par piece',
    ]);

    private readonly TAUX_REPORTS = new Set<string>([
        'total des retenues pour les clients',
        'total des retenues pour les fournisseurs',
    ]);

    /** Colonnes par défaut si le rapport n'a pas de configuration dédiée */
    readonly defaultTableColumns: string[] = [
        'Document',
        'Date',
        'Contact',
        'Total HT',
        'Remise',
        'Total TVA',
        'Total TTC',
        'Reste à payer',
    ];

    private readonly reportTableColumns = new Map<string, string[]>([
        [
            'vente par client',
            [
                'Type document',
                'Document',
                'Date',
                'Contact',
                'Total HT',
                'Remise',
                'Total TVA',
                'Total TTC',
                'Reste à payer',
            ],
        ],
        [
            'achat par fournisseur',
            [
                'Type document',
                'Document',
                'Date',
                'Fournisseur',
                'Total HT',
                'Remise',
                'Total TVA',
                'Total TTC',
                'Reste à payer',
            ],
        ],
        [
            'chiffre d affaires par produit',
            ['Référence', 'Produit', 'Quantité totale', "Chiffre d'affaire"],
        ],
        [
            'chiffre d affaire par produit',
            ['Référence', 'Produit', 'Quantité totale', "Chiffre d'affaire"],
        ],
        [
            'chiffre d affaires par categorie',
            ['Catégorie', "Chiffre d'affaire"],
        ],
        [
            'chiffre d affaire par categorie',
            ['Catégorie', "Chiffre d'affaire"],
        ],
        [
            'chiffre d affaires par produit par client',
            [
                'Code client',
                'Client',
                'Référence',
                'Produit',
                'Quantité',
                "Chiffre d'affaire",
            ],
        ],
        [
            'chiffre d affaire par produit par client',
            [
                'Code client',
                'Client',
                'Référence',
                'Produit',
                'Quantité',
                "Chiffre d'affaire",
            ],
        ],
        [
            'mouvement detaille de stock',
            [
                'Entrepôt',
                'Référence',
                'Produit',
                'Quantité',
                'Document',
                'Type',
                'Date',
                'Contact',
                'Quantité disponible',
            ],
        ],
        [
            'mouvement detaille de stcok',
            [
                'Entrepôt',
                'Référence',
                'Produit',
                'Quantité',
                'Document',
                'Type',
                'Date',
                'Contact',
                'Quantité disponible',
            ],
        ],
        [
            'benefice commercial par piece',
            [
                'Date',
                'Référence',
                "Chiffre d'affaires",
                "Prix d'achat",
                'Profit',
                "Prix d'achat (cump)",
                'Profit (cump)',
            ],
        ],
        [
            'benefice commercial mensuel',
            [
                'Mois',
                'Total ventes',
                'Total TVA',
                'Total achats',
                'Profit',
                'Total achats (cump)',
                'Profit (cump)',
            ],
        ],
        [
            'benefice commercial par ligne',
            [
                'Date',
                'Document',
                'Référence',
                'Produit',
                'Quantité',
                'Prix HT',
                "Prix d'achat",
                'Total achats',
                'Profit',
                'Valeur en stock',
                'Total (cump)',
                'Profit (cump)',
            ],
        ],
        [
            'benefice commercial par produit',
            [
                'Référence',
                'Produit',
                'Quantité vendu',
                "Chiffre d'affaires",
                "Prix d'achat",
                "Coût d'achats",
                'Profit',
                'Valeur de stock',
                "Coût d'achat (cump)",
                'Profit (cump)',
            ],
        ],
        [
            'chiffre d affaire par client',
            [
                'Référence',
                'Client',
                "Chiffre d'affaires",
                'Montant à payer',
                'Montant payé',
            ],
        ],
        [
            'chiffre d affaires par client',
            [
                'Référence',
                'Client',
                "Chiffre d'affaires",
                'Montant à payer',
                'Montant payé',
            ],
        ],
        [
            'chiffre d affaires par fournisseur',
            [
                'Référence',
                'Contact',
                "Chiffre d'affaires",
                'Montant à payer',
                'Montant payé',
            ],
        ],
        [
            'chiffre d affaire par fournisseur',
            [
                'Référence',
                'Contact',
                "Chiffre d'affaires",
                'Montant à payer',
                'Montant payé',
            ],
        ],
        [
            'paiement clients',
            [
                'Date',
                'Contact',
                'Méthode de paiement',
                'Total HT',
                'Destination',
            ],
        ],
        [
            'facture clients payees depuis plus d un mois',
            [
                'Date',
                'Référence',
                'Contact',
                'État',
                'Total TTC',
                'Net à payer',
            ],
        ],
        [
            'details de ventes par ligne de produit',
            [
                'Document',
                'Date',
                'Contact',
                'Référence',
                'Produit',
                'Quantité',
                'Prix unitaire',
                'Prix HT',
                'Remise',
                'TVA',
                'Total TTC',
            ],
        ],
        [
            'details ventes par ligne produit',
            [
                'Document',
                'Date',
                'Contact',
                'Référence',
                'Produit',
                'Quantité',
                'Prix unitaire',
                'Prix HT',
                'Remise',
                'TVA',
                'Total TTC',
            ],
        ],
        [
            'transaction client',
            [
                'Date',
                'Contact',
                'Référence',
                'Méthode de paiement',
                'Montant',
            ],
        ],
        [
            'transactions client',
            [
                'Date',
                'Contact',
                'Référence',
                'Méthode de paiement',
                'Montant',
            ],
        ],
        [
            'transactions clients',
            [
                'Date',
                'Contact',
                'Référence',
                'Méthode de paiement',
                'Montant',
            ],
        ],
        [
            'tva de vente',
            [
                'Document',
                'Date',
                'Contact',
                'Total',
            ],
        ],
        [
            'tva ventes',
            [
                'Document',
                'Date',
                'Contact',
                'Total',
            ],
        ],
        [
            'total des retenues pour les clients',
            [
                'Matricule fiscal',
                'Nom complet',
                'Adresse',
                'Date',
                'Montant brut',
                'Taux',
                'Montant',
                'Net à payer',
            ],
        ],
        [
            'details d achat par ligne produit',
            [
                'Document',
                'Date',
                'Contact',
                'Référence',
                'Produit',
                'Quantité',
                'Prix unitaire',
                'Total HT',
                'Remise',
                'TVA',
                'Total TTC',
            ],
        ],
        [
            'details d achat par ligne de produit',
            [
                'Document',
                'Date',
                'Contact',
                'Référence',
                'Produit',
                'Quantité',
                'Prix unitaire',
                'Total HT',
                'Remise',
                'TVA',
                'Total TTC',
            ],
        ],
        [
            'transactions fournisseurs',
            [
                'Date',
                'Contact',
                'Référence',
                'Méthode de paiement',
                'Montant',
            ],
        ],
        [
            'tva achats',
            [
                'Document',
                'Date',
                'Contact',
                'Total',
            ],
        ],
        [
            'tva achat',
            [
                'Document',
                'Date',
                'Contact',
                'Total',
            ],
        ],
        [
            'total des retenues pour les fournisseurs',
            [
                'Matricule fiscal',
                'Nom complet',
                'Adresse',
                'Date',
                'Montant brut',
                'Taux',
                'Montant',
                'Net à payer',
            ],
        ],
    ]);

    get tableColumns(): string[] {
        return this.reportTableColumns.get(this.normalizedReportName) ?? this.defaultTableColumns;
    }

    /**
     * Lignes affichées dans le tableau : pagination pour « Vente par client » / « Achat par fournisseur »
     * et « Détails …/par ligne produit ». Sinon, toutes les lignes.
     */
    get reportTableRows(): any[] {
        if (this.isDetailsLignesProduitReport) {
            // Pagination côté serveur : la liste reçue correspond déjà à la page courante
            return this.data;
        }
        if (!this.isVenteParClientReport && !this.isAchatParFournisseurReport) {
            return this.data;
        }
        const start = (this.venteParClientCurrentPage - 1) * this.venteParClientPageSize;
        return this.data.slice(start, start + this.venteParClientPageSize);
    }

    /** Pagination serveur : l’API lignes/vente|achat renvoie une page à la fois */
    private isDetailsLignesProduitPaginationServerSide(): boolean {
        return this.isDetailsLignesProduitReport;
    }

    /** TVA vente / TVA achats — même présentation (capture) */
    get isTvaVenteOuAchatsReport(): boolean {
        const k = this.normalizedReportName;
        return (
            k === 'tva achats' ||
            k === 'tva achat' ||
            k === 'tva de vente' ||
            k === 'tva ventes'
        );
    }

    /** Affichage cellule lorsque `data` sera alimenté (tableau de lignes ou d'objets indexés). */
    getRowCell(row: any, columnIndex: number, columnLabel: string): string {
        if (row == null) {
            return '';
        }
        if (Array.isArray(row)) {
            const v = row[columnIndex];
            return v != null && v !== '' ? String(v) : '';
        }
        if (typeof row === 'object') {
            const byIndex = row[columnIndex];
            if (byIndex != null && byIndex !== '') {
                return String(byIndex);
            }
            const byLabel = row[columnLabel];
            if (byLabel != null && byLabel !== '') {
                return String(byLabel);
            }
        }
        return '';
    }

    tdClasses(first: boolean, last: boolean): Record<string, boolean> {
        if (this.isTvaVenteOuAchatsReport) {
            return {
                'tva-report-td-doc': first,
                'tva-report-td-mid': !first && !last,
                'tva-report-td-num': last,
                'border-start-cell': first,
                'border-end-cell': last,
            };
        }
        return {
            'border-start-cell': first,
            'border-end-cell': last,
            'text-end': !first,
            'text-primary': !first,
            'fw-bold': !first,
        };
    }

    get showContactSelect(): boolean {
        return this.CONTACT_REPORTS.has(this.normalizedReportName);
    }

    get showProductSelect(): boolean {
        return this.PRODUCT_REPORTS.has(this.normalizedReportName);
    }

    get showGroupBySelect(): boolean {
        return this.GROUP_BY_REPORTS.has(this.normalizedReportName);
    }

    get showDocumentSelect(): boolean {
        return this.DOCUMENT_REPORTS.has(this.normalizedReportName);
    }

    get showTauxInput(): boolean {
        return this.TAUX_REPORTS.has(this.normalizedReportName);
    }

    get showRetenuesFournisseurContactBanner(): boolean {
        return (
            this.normalizedReportName === 'total des retenues pour les fournisseurs' &&
            !this.retenuesFournisseurBannerDismissed
        );
    }

    /** Rapport « Vente par client » : liste des documents de vente filtrés par client */
    get isVenteParClientReport(): boolean {
        return this.normalizedReportName === 'vente par client';
    }

    /** Rapport « Achat par fournisseur » : documents d’achat filtrés par fournisseur */
    get isAchatParFournisseurReport(): boolean {
        return this.normalizedReportName === 'achat par fournisseur';
    }

    /** Rapport « Détails ventes par ligne produit » : lignes de vente filtrées par produit */
    get isDetailsVentesParLigneProduitReport(): boolean {
        return (
            this.normalizedReportName === 'details ventes par ligne produit' ||
            this.normalizedReportName === 'details de ventes par ligne de produit'
        );
    }

    /** Rapport « Détails d'achat par ligne produit » : lignes d'achat filtrées par produit/fournisseur */
    get isDetailsAchatParLigneProduitReport(): boolean {
        return (
            this.normalizedReportName === 'details d achat par ligne produit' ||
            this.normalizedReportName === 'details d achat par ligne de produit'
        );
    }

    /** L'un des deux rapports « Détails …/par ligne produit » (ventes ou achat) */
    get isDetailsLignesProduitReport(): boolean {
        return this.isDetailsVentesParLigneProduitReport || this.isDetailsAchatParLigneProduitReport;
    }

    /** Vrai pour les rapports qui présentent un sélecteur fournisseur (et non client) */
    get useFournisseurContactSelect(): boolean {
        return this.isAchatParFournisseurReport || this.isDetailsAchatParLigneProduitReport;
    }

    /** Pagination documents (vente, achat, détails par ligne produit) — mêmes contrôles */
    get isDocumentsRapportPaginated(): boolean {
        return (
            this.isVenteParClientReport ||
            this.isAchatParFournisseurReport ||
            this.isDetailsLignesProduitReport
        );
    }

    get isLoadingRapportDocuments(): boolean {
        return (
            this.isLoadingVenteParClientDocs ||
            this.isLoadingAchatFournisseurDocs ||
            this.isLoadingDetailsVentesParProduitDocs ||
            this.isLoadingDetailsAchatParProduitDocs
        );
    }

    dismissRetenuesFournisseurBanner(): void {
        this.retenuesFournisseurBannerDismissed = true;
    }

    /** Total pied de tableau TVA (à brancher sur l’API) */
    tvaFooterTotalDisplay = '0,000 TND';

    tvaPageSize = 10;
    readonly tvaPageSizeOptions = [10, 25, 50, 100];
    tvaCurrentPage = 1;

    incrementTaux(): void {
        const next = (Number(this.taux ?? 0) || 0) + this.tauxStep;
        this.taux = Math.max(0, next);
    }

    decrementTaux(): void {
        const next = (Number(this.taux ?? 0) || 0) - this.tauxStep;
        this.taux = Math.max(0, next);
    }

    private normalizeReportName(value: string): string {
        return (value || '')
            .toString()
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            /* Apostrophes typographiques / ASCII / guillemets → espace (comme dans les libellés du menu) */
            .replace(/[\u2019\u2018\u0027\u02BC\u0060"]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }
  constructor(
      private router: Router,
      private route: ActivatedRoute,
      private headerService: HeaderService,
      private clientService: ClientService,
      private fournisseurService: FournisseurService,
      private productsService: ProductsService,
      private documentService: DocumentService,
      private toastr: ToastrService
  ) {}

  ngOnInit(): void {
      this.loadStationFromUser();
      this.route.queryParams.subscribe(params => {
          const nameFromRoute = (params['name'] || '').toString() || 'Rapport';
          if (nameFromRoute !== this.lastReportQueryName) {
              this.lastReportQueryName = nameFromRoute;
              this.retenuesFournisseurBannerDismissed = false;
              this.selectedContactId = null;
              this.selectedGroupById = null;
              this.selectedProductId = null;
              this.selectedDocumentType = null;
              this.data = [];
          }
          this.reportName = nameFromRoute;
          this.normalizedReportName = this.normalizeReportName(this.reportName);
          this.headerService.setConfig({
              title: this.reportName,
              buttons: [
                  {
                      label: 'Exporter',
                      // action: () => this.exportData(),
                      type: 'secondary'
                  }
              ]
          });
          if (this.CONTACT_REPORTS.has(this.normalizedReportName)) {
              if (this.useFournisseurContactSelect) {
                  this.loadRapportFournisseurs();
              } else {
                  this.loadContactClients();
              }
          }
          if (this.PRODUCT_REPORTS.has(this.normalizedReportName)) {
              this.loadRapportProducts();
          }
          if (this.isVenteParClientReport) {
              this.loadVenteParClientDocuments();
          }
          if (this.isAchatParFournisseurReport) {
              this.loadAchatParFournisseurDocuments();
          }
          if (this.isDetailsVentesParLigneProduitReport) {
              this.venteParClientCurrentPage = 1;
              this.detailsVentesParProduitTotal = 0;
              this.loadDetailsVentesParLigneProduit();
          }
          if (this.isDetailsAchatParLigneProduitReport) {
              this.venteParClientCurrentPage = 1;
              this.detailsAchatParProduitTotal = 0;
              this.loadDetailsAchatParLigneProduit();
          }
      });

  }

  /** Appelé lorsque l'utilisateur change le produit sélectionné */
  onRapportProductChange(_id: number | null): void {
      if (this.isDetailsLignesProduitReport) {
          this.venteParClientCurrentPage = 1;
          this.reloadCurrentDetailsLignesProduit();
      }
  }

    /**
     * Appelé lorsque l’utilisateur change le contact (client ou fournisseur selon le rapport).
     */
    onRapportContactChange(_id: number | null): void {
        if (this.isVenteParClientReport) {
            this.loadVenteParClientDocuments();
        }
        if (this.isAchatParFournisseurReport) {
            this.loadAchatParFournisseurDocuments();
        }
        if (this.isDetailsLignesProduitReport) {
            this.venteParClientCurrentPage = 1;
            this.reloadCurrentDetailsLignesProduit();
        }
    }

    /** Client sélectionné (filtre supplémentaire pour « Détails ventes par ligne produit ») */
    private hasDetailsVentesClientFilter(): boolean {
        const n = Number(this.selectedContactId);
        return Number.isFinite(n) && n > 0;
    }

    /**
     * Sans client : tous les documents de vente de la station.
     * Avec client : documents de ce client uniquement (types vente 7–11).
     */
    loadVenteParClientDocuments(): void {
        if (!this.isVenteParClientReport) {
            return;
        }
        if (!this.stationId) {
            this.data = [];
            this.updateVenteParClientPagination();
            return;
        }

        this.isLoadingVenteParClientDocs = true;

        if (!this.selectedContactId) {
            this.documentService
                .getAllVenteDocumentsForStation(this.stationId, 200)
                .pipe(finalize(() => (this.isLoadingVenteParClientDocs = false)))
                .subscribe({
                    next: (docs: any[]) => {
                        const filtered = (docs || []).filter((d) => {
                            const tid = Number(d?.type_document_id ?? d?.typeDocument?.id);
                            return Number.isFinite(tid) && this.VENTE_DOCUMENT_TYPE_IDS.has(tid);
                        });
                        this.data = filtered.map((d) => this.mapDocumentRowVenteParClient(d));
                        this.venteParClientCurrentPage = 1;
                        this.updateVenteParClientPagination();
                    },
                    error: (err) => {
                        console.error('getAllVenteDocumentsForStation (vente par client):', err);
                        this.toastr.error(
                            err?.error?.message || 'Impossible de charger les documents de vente.',
                            'Erreur'
                        );
                        this.data = [];
                        this.updateVenteParClientPagination();
                    }
                });
            return;
        }

        this.documentService
            .getDocumentsByClientAll(this.selectedContactId, 200)
            .pipe(finalize(() => (this.isLoadingVenteParClientDocs = false)))
            .subscribe({
                next: (docs: any[]) => {
                    const filtered = (docs || []).filter((d) => {
                        const tid = Number(d?.type_document_id ?? d?.typeDocument?.id);
                        return Number.isFinite(tid) && this.VENTE_DOCUMENT_TYPE_IDS.has(tid);
                    });
                    filtered.sort((a, b) => {
                        const da = new Date(a?.date || a?.date_creation || a?.created_at || 0).getTime();
                        const db = new Date(b?.date || b?.date_creation || b?.created_at || 0).getTime();
                        return db - da;
                    });
                    this.data = filtered.map((d) => this.mapDocumentRowVenteParClient(d));
                    this.venteParClientCurrentPage = 1;
                    this.updateVenteParClientPagination();
                },
                error: (err) => {
                    console.error('getDocumentsByClientAll (vente par client):', err);
                    this.toastr.error(
                        err?.error?.message || 'Impossible de charger les documents du client.',
                        'Erreur'
                    );
                    this.data = [];
                    this.updateVenteParClientPagination();
                }
            });
    }

    /**
     * Sans fournisseur : tous les documents d’achat de la station (types 12–14).
     * Avec fournisseur : documents de ce fournisseur uniquement.
     */
    loadAchatParFournisseurDocuments(): void {
        if (!this.isAchatParFournisseurReport) {
            return;
        }
        if (!this.stationId) {
            this.data = [];
            this.updateVenteParClientPagination();
            return;
        }

        this.isLoadingAchatFournisseurDocs = true;

        if (!this.selectedContactId) {
            this.documentService
                .getAllAchatDocumentsForStation(this.stationId, 200)
                .pipe(finalize(() => (this.isLoadingAchatFournisseurDocs = false)))
                .subscribe({
                    next: (docs: any[]) => {
                        const filtered = (docs || []).filter((d) => {
                            const tid = Number(d?.type_document_id ?? d?.typeDocument?.id);
                            return Number.isFinite(tid) && this.ACHAT_DOCUMENT_TYPE_IDS.has(tid);
                        });
                        this.data = filtered.map((d) => this.mapDocumentRowAchatParFournisseur(d));
                        this.venteParClientCurrentPage = 1;
                        this.updateVenteParClientPagination();
                    },
                    error: (err) => {
                        console.error('getAllAchatDocumentsForStation (achat par fournisseur):', err);
                        this.toastr.error(
                            err?.error?.message || 'Impossible de charger les documents d’achat.',
                            'Erreur'
                        );
                        this.data = [];
                        this.updateVenteParClientPagination();
                    }
                });
            return;
        }

        this.documentService
            .getDocumentsByFournisseurAll(this.selectedContactId, 200)
            .pipe(finalize(() => (this.isLoadingAchatFournisseurDocs = false)))
            .subscribe({
                next: (docs: any[]) => {
                    const filtered = (docs || []).filter((d) => {
                        const tid = Number(d?.type_document_id ?? d?.typeDocument?.id);
                        return Number.isFinite(tid) && this.ACHAT_DOCUMENT_TYPE_IDS.has(tid);
                    });
                    filtered.sort((a, b) => {
                        const da = new Date(a?.date || a?.date_creation || a?.created_at || 0).getTime();
                        const db = new Date(b?.date || b?.date_creation || b?.created_at || 0).getTime();
                        return db - da;
                    });
                    this.data = filtered.map((d) => this.mapDocumentRowAchatParFournisseur(d));
                    this.venteParClientCurrentPage = 1;
                    this.updateVenteParClientPagination();
                },
                error: (err) => {
                    console.error('getDocumentsByFournisseurAll (achat par fournisseur):', err);
                    this.toastr.error(
                        err?.error?.message || 'Impossible de charger les documents du fournisseur.',
                        'Erreur'
                    );
                    this.data = [];
                    this.updateVenteParClientPagination();
                }
            });
    }

    private mapDocumentRowAchatParFournisseur(doc: any): string[] {
        const typeId = Number(doc?.type_document_id ?? doc?.typeDocument?.id);
        const typeLabel = this.labelTypeDocumentAchat(typeId, doc);
        const ref = doc?.ref_externe ?? doc?.reference ?? (doc?.id != null ? `#${doc.id}` : '—');
        const dateStr = this.formatDateDocument(doc?.date || doc?.date_creation || doc?.created_at);
        const fournisseur = this.labelFournisseurFromDocument(doc);
        return [
            typeLabel,
            ref,
            dateStr,
            fournisseur,
            this.formatMontantTnd(doc?.total_ht),
            this.formatRemiseDocument(doc),
            this.formatMontantTnd(doc?.total_tva),
            this.formatMontantTnd(doc?.total_ttc),
            this.formatMontantTnd(doc?.net_payer ?? doc?.net_a_payer),
        ];
    }

    private labelTypeDocumentAchat(typeId: number, doc: any): string {
        const byId: Record<number, string> = {
            12: 'Commande fournisseur',
            13: 'Bon de réception',
            14: 'Facture fournisseur',
        };
        if (Number.isFinite(typeId) && byId[typeId]) {
            return byId[typeId];
        }
        const t = doc?.typeDocument?.titre || doc?.type_document?.titre;
        return t ? String(t) : '—';
    }

    private labelFournisseurFromDocument(doc: any): string {
        const f =
            doc?.fornisseurs?.[0] ??
            doc?.fournisseurs?.[0] ??
            doc?.fournisseur_data ??
            doc?.fournisseur;
        const nom = f?.nom_entreprise ?? f?.nom_fournisseur ?? f?.raison_sociale;
        return nom ? String(nom) : '—';
    }

    private updateVenteParClientPagination(): void {
        if (this.isDetailsLignesProduitReport) {
            const totalSource = this.isDetailsVentesParLigneProduitReport
                ? this.detailsVentesParProduitTotal
                : this.detailsAchatParProduitTotal;
            const total = this.isDetailsLignesProduitPaginationServerSide()
                ? Math.max(0, Number(totalSource) || 0)
                : this.data.length;
            this.venteParClientTotalPages =
                Math.ceil(total / this.venteParClientPageSize) || 1;
            this.venteParClientCurrentPage = Math.min(
                Math.max(1, this.venteParClientCurrentPage),
                this.venteParClientTotalPages
            );
            this.venteParClientPages = Array.from(
                { length: this.venteParClientTotalPages },
                (_, i) => i + 1
            );
            return;
        }
        if (!this.isVenteParClientReport && !this.isAchatParFournisseurReport) {
            return;
        }
        this.venteParClientTotalPages =
            Math.ceil(this.data.length / this.venteParClientPageSize) || 1;
        this.venteParClientCurrentPage = Math.min(
            Math.max(1, this.venteParClientCurrentPage),
            this.venteParClientTotalPages
        );
        this.venteParClientPages = Array.from(
            { length: this.venteParClientTotalPages },
            (_, i) => i + 1
        );
    }

    changeVenteParClientPage(page: number): void {
        this.venteParClientCurrentPage = page;
        if (this.isDetailsLignesProduitPaginationServerSide()) {
            this.reloadCurrentDetailsLignesProduit();
            return;
        }
        this.updateVenteParClientPagination();
    }

    onVenteParClientPageSizeChange(): void {
        this.venteParClientCurrentPage = 1;
        if (this.isDetailsLignesProduitPaginationServerSide()) {
            this.reloadCurrentDetailsLignesProduit();
            return;
        }
        this.updateVenteParClientPagination();
    }

    /** Recharge le rapport « Détails …/par ligne produit » courant. */
    private reloadCurrentDetailsLignesProduit(): void {
        if (this.isDetailsVentesParLigneProduitReport) {
            this.loadDetailsVentesParLigneProduit();
        } else if (this.isDetailsAchatParLigneProduitReport) {
            this.loadDetailsAchatParLigneProduit();
        }
    }

    /** Produit sélectionné pour le rapport « Détails ventes par ligne produit » (query product_id) */
    private hasDetailsVentesProductFilter(): boolean {
        const n = Number(this.selectedProductId);
        return Number.isFinite(n) && n > 0;
    }

    /**
     * Rapport « Détails ventes par ligne produit » : API unifiée
     * GET …/lignes/vente (product_id, client_id, page, limit selon les filtres).
     */
    loadDetailsVentesParLigneProduit(): void {
        if (!this.isDetailsVentesParLigneProduitReport) {
            return;
        }
        this.loadDetailsVentesLignesFromApi();
    }

    private loadDetailsVentesLignesFromApi(): void {
        const hasProduct = this.hasDetailsVentesProductFilter();
        const hasClient = this.hasDetailsVentesClientFilter();
        const productId = Number(this.selectedProductId);

        if (hasProduct && (!Number.isFinite(productId) || productId <= 0)) {
            this.data = [];
            this.detailsVentesParProduitTotal = 0;
            this.updateVenteParClientPagination();
            return;
        }

        if (hasProduct && !this.stationId) {
            this.data = [];
            this.detailsVentesParProduitTotal = 0;
            this.updateVenteParClientPagination();
            return;
        }

        if (!hasProduct && !hasClient && !this.stationId) {
            this.data = [];
            this.detailsVentesParProduitTotal = 0;
            this.updateVenteParClientPagination();
            return;
        }

        const params: {
            product_id?: number;
            client_id?: number;
            page: number;
            limit: number;
            station_id?: number;
            user_id?: number;
        } = {
            page: this.venteParClientCurrentPage,
            limit: this.venteParClientPageSize,
        };
        if (this.stationId > 0) {
            params.station_id = this.stationId;
        }
        if (this.userId > 0) {
            params.user_id = this.userId;
        }
        if (hasProduct) {
            params.product_id = productId;
        }
        if (hasClient) {
            params.client_id = Number(this.selectedContactId);
        }

        this.isLoadingDetailsVentesParProduitDocs = true;
        this.documentService
            .getDocumentsLignesVente(params)
            .pipe(finalize(() => (this.isLoadingDetailsVentesParProduitDocs = false)))
            .subscribe({
                next: (res: any) => {
                    const lignes = this.extractLignesArrayFromLignesVenteResponse(res);
                    const pagination = this.extractPaginationFromLignesVenteResponse(res);
                    this.detailsVentesParProduitTotal =
                        Number(
                            pagination?.total ??
                                pagination?.total_items ??
                                pagination?.totalItems ??
                                lignes.length
                        ) || lignes.length;
                    this.data = lignes.map((l) => this.mapLigneVenteApiToTableRow(l));
                    this.updateVenteParClientPagination();
                },
                error: (err) => {
                    console.error('getDocumentsLignesVente:', err);
                    this.toastr.error(
                        err?.error?.message || 'Impossible de charger les lignes de vente.',
                        'Erreur'
                    );
                    this.data = [];
                    this.detailsVentesParProduitTotal = 0;
                    this.updateVenteParClientPagination();
                },
            });
    }

    private extractLignesArrayFromLignesVenteResponse(res: any): any[] {
        if (!res) {
            return [];
        }
        if (Array.isArray(res)) {
            return res;
        }
        if (Array.isArray(res?.data)) {
            return res.data;
        }
        if (Array.isArray(res?.data?.lignes)) {
            return res.data.lignes;
        }
        if (Array.isArray(res?.data?.data)) {
            return res.data.data;
        }
        if (Array.isArray(res?.lignes)) {
            return res.lignes;
        }
        if (Array.isArray(res?.rows)) {
            return res.rows;
        }
        return [];
    }

    private extractPaginationFromLignesVenteResponse(res: any): any {
        return (
            res?.pagination ??
            res?.data?.pagination ??
            res?.meta ??
            res?.data?.meta ??
            null
        );
    }

    /** Mappe une ligne renvoyée par /lignes/vente vers les colonnes du tableau. */
    private mapLigneVenteApiToTableRow(line: any): string[] {
        const doc = line?.document ?? line?.document_data ?? line?.doc ?? null;
        const ref =
            line?.document_reference ??
            line?.ref_document ??
            doc?.ref_externe ??
            doc?.reference ??
            (doc?.id != null ? `#${doc.id}` : '—');
        const dateStr = this.formatDateDocument(
            line?.date ??
                line?.date_document ??
                line?.date_ligne ??
                doc?.date ??
                doc?.date_creation ??
                doc?.created_at
        );
        const contact =
            line?.nom_client ??
            line?.client_nom ??
            line?.nom_prenom ??
            line?.raison_sociale ??
            (doc ? this.labelContactFromDocument(doc) : '—');
        const productLabel =
            line?.nom_produit ??
            line?.designation ??
            line?.libelle ??
            line?.product?.nom ??
            line?.produit?.nom ??
            '—';
        const productRef =
            line?.ref_interne ??
            line?.reference_produit ??
            line?.product?.ref_interne ??
            line?.produit?.ref_interne ??
            line?.reference ??
            '—';
        const quantite = line?.quantite ?? line?.quantity ?? line?.qte ?? '—';
        const prixUnit = line?.prix_unitaire ?? line?.prix_ht ?? line?.prix ?? null;
        const prixHt = line?.total_ht ?? line?.prix_total_ht ?? null;
        const remise = line?.remise ?? line?.remise_ligne ?? line?.remise_montant ?? null;
        const tva = line?.tva ?? line?.total_tva ?? line?.montant_tva ?? null;
        const totalTtc = line?.total_ttc ?? line?.prix_total_ttc ?? null;

        return [
            ref != null ? String(ref) : '—',
            dateStr,
            contact != null ? String(contact) : '—',
            productRef != null ? String(productRef) : '—',
            productLabel != null ? String(productLabel) : '—',
            this.formatQuantite(quantite),
            this.formatMontantTnd(prixUnit),
            this.formatMontantTnd(prixHt),
            this.formatMontantTnd(remise),
            this.formatMontantTnd(tva),
            this.formatMontantTnd(totalTtc),
        ];
    }

    /** Fournisseur sélectionné pour le rapport « Détails d'achat par ligne produit » */
    private hasDetailsAchatFournisseurFilter(): boolean {
        const n = Number(this.selectedContactId);
        return Number.isFinite(n) && n > 0;
    }

    /**
     * Rapport « Détails d'achat par ligne produit » : API unifiée
     * GET …/lignes/achat (product_id, fournisseur_id, page, limit selon les filtres).
     * Sans aucun filtre, GET …/achat/lignes (page, limit).
     */
    loadDetailsAchatParLigneProduit(): void {
        if (!this.isDetailsAchatParLigneProduitReport) {
            return;
        }

        const hasProduct = this.hasDetailsVentesProductFilter();
        const hasFournisseur = this.hasDetailsAchatFournisseurFilter();
        const productId = Number(this.selectedProductId);

        const params: {
            product_id?: number;
            fournisseur_id?: number;
            page: number;
            limit: number;
            station_id?: number;
            user_id?: number;
        } = {
            page: this.venteParClientCurrentPage,
            limit: this.venteParClientPageSize,
        };
        if (this.stationId > 0) {
            params.station_id = this.stationId;
        }
        if (this.userId > 0) {
            params.user_id = this.userId;
        }
        if (hasProduct && Number.isFinite(productId) && productId > 0) {
            params.product_id = productId;
        }
        if (hasFournisseur) {
            params.fournisseur_id = Number(this.selectedContactId);
        }

        this.isLoadingDetailsAchatParProduitDocs = true;
        this.documentService
            .getDocumentsLignesAchat(params)
            .pipe(finalize(() => (this.isLoadingDetailsAchatParProduitDocs = false)))
            .subscribe({
                next: (res: any) => {
                    const lignes = this.extractLignesArrayFromLignesVenteResponse(res);
                    const pagination = this.extractPaginationFromLignesVenteResponse(res);
                    this.detailsAchatParProduitTotal =
                        Number(
                            pagination?.total ??
                                pagination?.total_items ??
                                pagination?.totalItems ??
                                lignes.length
                        ) || lignes.length;
                    this.data = lignes.map((l) => this.mapLigneAchatApiToTableRow(l));
                    this.updateVenteParClientPagination();
                },
                error: (err) => {
                    console.error('getDocumentsLignesAchat:', err);
                    this.toastr.error(
                        err?.error?.message || 'Impossible de charger les lignes d’achat.',
                        'Erreur'
                    );
                    this.data = [];
                    this.detailsAchatParProduitTotal = 0;
                    this.updateVenteParClientPagination();
                },
            });
    }

    /** Mappe une ligne renvoyée par /lignes/achat (ou /achat/lignes) vers les colonnes du tableau. */
    private mapLigneAchatApiToTableRow(line: any): string[] {
        const doc = line?.document ?? line?.document_data ?? line?.doc ?? null;
        const ref =
            line?.document_reference ??
            line?.ref_document ??
            doc?.ref_externe ??
            doc?.reference ??
            (doc?.id != null ? `#${doc.id}` : '—');
        const dateStr = this.formatDateDocument(
            line?.date ??
                line?.date_document ??
                line?.date_ligne ??
                doc?.date ??
                doc?.date_creation ??
                doc?.created_at
        );
        const contact =
            line?.fornisseurs?.[0]?.nom_entreprise ??
            line?.fournisseurs?.[0]?.nom_entreprise ??
            line?.fornisseur?.nom_entreprise ??
            line?.fournisseur?.nom_entreprise ??
            line?.fournisseur_data?.nom_entreprise ??
            doc?.fornisseurs?.[0]?.nom_entreprise ??
            doc?.fournisseurs?.[0]?.nom_entreprise ??
            line?.nom_entreprise ??
            line?.nom_fournisseur ??
            line?.fournisseur_nom ??
            line?.raison_sociale ??
            (doc ? this.labelFournisseurFromDocument(doc) : '—');
        const productLabel =
            line?.nom_produit ??
            line?.designation ??
            line?.libelle ??
            line?.product?.nom ??
            line?.produit?.nom ??
            '—';
        const productRef =
            line?.ref_interne ??
            line?.reference_produit ??
            line?.product?.ref_interne ??
            line?.produit?.ref_interne ??
            line?.reference ??
            '—';
        const quantite =
            line?.qnt ??
            line?.qty ??
            line?.quantite ??
            line?.quantity ??
            line?.qte ??
            line?.ligne?.qnt ??
            line?.ligne_document?.qnt ??
            null;
        const prixUnit = line?.prix_unitaire ?? line?.prix_ht ?? line?.prix ?? null;
        const prixHt = line?.total_ht ?? line?.prix_total_ht ?? null;
        const remise = line?.remise ?? line?.remise_ligne ?? line?.remise_montant ?? null;
        const tva = line?.tva ?? line?.total_tva ?? line?.montant_tva ?? null;
        const totalTtc = line?.total_ttc ?? line?.prix_total_ttc ?? null;

        return [
            ref != null ? String(ref) : '—',
            dateStr,
            contact != null ? String(contact) : '—',
            productRef != null ? String(productRef) : '—',
            productLabel != null ? String(productLabel) : '—',
            this.formatQuantite(quantite),
            this.formatMontantTnd(prixUnit),
            this.formatMontantTnd(prixHt),
            this.formatMontantTnd(remise),
            this.formatMontantTnd(tva),
            this.formatMontantTnd(totalTtc),
        ];
    }

    private mapDocumentRowVenteParClient(doc: any): string[] {
        const typeId = Number(doc?.type_document_id ?? doc?.typeDocument?.id);
        const typeLabel = this.labelTypeDocumentVente(typeId, doc);
        const ref = doc?.ref_externe ?? doc?.reference ?? (doc?.id != null ? `#${doc.id}` : '—');
        const dateStr = this.formatDateDocument(doc?.date || doc?.date_creation || doc?.created_at);
        const contact = this.labelContactFromDocument(doc);
        return [
            typeLabel,
            ref,
            dateStr,
            contact,
            this.formatMontantTnd(doc?.total_ht),
            this.formatRemiseDocument(doc),
            this.formatMontantTnd(doc?.total_tva),
            this.formatMontantTnd(doc?.total_ttc),
            this.formatMontantTnd(doc?.net_payer ?? doc?.net_a_payer),
        ];
    }

    private labelTypeDocumentVente(typeId: number, doc: any): string {
        const byId: Record<number, string> = {
            7: 'Devis',
            8: 'Commande client',
            9: 'Bon de sortie',
            10: 'Bon de livraison',
            11: 'Facture',
        };
        if (Number.isFinite(typeId) && byId[typeId]) {
            return byId[typeId];
        }
        const t = doc?.typeDocument?.titre || doc?.type_document?.titre;
        return t ? String(t) : '—';
    }

    private labelContactFromDocument(doc: any): string {
        const c = doc?.clients?.[0] ?? doc?.client_data ?? doc?.client;
        const nom = c?.nom_prenom ?? c?.nom_client ?? c?.nom_entreprise;
        return nom ? String(nom) : '—';
    }

    private formatDateDocument(value: any): string {
        if (value == null || value === '') {
            return '—';
        }
        const d = new Date(value);
        if (isNaN(d.getTime())) {
            return '—';
        }
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    /**
     * Affichage d’une quantité sans zéros décimaux superflus.
     * Ex. "3.000" → "3", "1.500" → "1,5", "2,250" → "2,25".
     */
    private formatQuantite(value: any): string {
        if (value == null || value === '') {
            return '—';
        }
        const n =
            typeof value === 'number'
                ? value
                : parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
        if (isNaN(n)) {
            return String(value);
        }
        const formatted = n.toLocaleString('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
        });
        return formatted;
    }

    private formatMontantTnd(value: any): string {
        if (value == null || value === '') {
            return '—';
        }
        const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
        if (isNaN(n)) {
            return '—';
        }
        return (
            n.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' TND'
        );
    }

    private formatRemiseDocument(doc: any): string {
        const r = doc?.remise ?? doc?.total_remise ?? doc?.remise_globale;
        if (r == null || r === '') {
            return '—';
        }
        const n = typeof r === 'number' ? r : parseFloat(String(r).replace(/\s/g, '').replace(',', '.'));
        if (isNaN(n)) {
            return String(r);
        }
        return (
            n.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' TND'
        );
    }

    private loadStationFromUser(): void {
        try {
            const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
            this.stationId = user?.stations?.[0]?.id ?? 0;
            const uid = user?.id;
            this.userId = typeof uid === 'number' && Number.isFinite(uid) ? uid : parseInt(String(uid ?? '0'), 10) || 0;
        } catch {
            this.stationId = 0;
            this.userId = 0;
        }
    }

    /** Clients actifs pour le select « contact » (rapports hors achat fournisseur) */
    loadContactClients(): void {
        if (!this.stationId) {
            this.contactClients = [];
            return;
        }
        this.rapportFournisseurs = [];
        this.isLoadingContacts = true;
        this.clientService.getActiveClient(this.stationId, 1, 1000).subscribe({
            next: (res: PaginatedResponse<Client>) => {
                this.contactClients = res.data ?? [];
                this.isLoadingContacts = false;
            },
            error: (err) => {
                console.error('getActiveClient (rapport):', err);
                this.toastr.error(
                    err?.error?.message || 'Impossible de charger les clients.',
                    'Erreur'
                );
                this.contactClients = [];
                this.isLoadingContacts = false;
            }
        });
    }

    /** Fournisseurs actifs pour le rapport « Achat par fournisseur » */
    loadRapportFournisseurs(): void {
        if (!this.stationId) {
            this.rapportFournisseurs = [];
            return;
        }
        this.contactClients = [];
        this.isLoadingContacts = true;
        this.fournisseurService.getActiveFournisseur(this.stationId, 1, 1000).subscribe({
            next: (res: PaginatedResponse<Fournisseur>) => {
                this.rapportFournisseurs = res.data ?? [];
                this.isLoadingContacts = false;
            },
            error: (err) => {
                console.error('getActiveFournisseur (rapport):', err);
                this.toastr.error(
                    err?.error?.message || 'Impossible de charger les fournisseurs.',
                    'Erreur'
                );
                this.rapportFournisseurs = [];
                this.isLoadingContacts = false;
            }
        });
    }

    /** Produits actifs pour le select « produit » (uniquement ceux ayant un code-barres) */
    loadRapportProducts(): void {
        if (!this.stationId) {
            this.rapportProducts = [];
            return;
        }
        this.isLoadingProducts = true;
        this.productsService.getActiveProductsByStationId(this.stationId, 1, 1000, false).subscribe({
            next: (res: PaginatedResponse<Product>) => {
                const all = res.data ?? [];
                this.rapportProducts = all.filter((p: any) => {
                    const code = p?.code_bar ?? p?.code_barre;
                    return code !== null && code !== undefined && String(code).trim() !== '';
                });
                this.isLoadingProducts = false;
            },
            error: (err) => {
                console.error('getActiveProductsByStationId (rapport):', err);
                this.toastr.error(
                    err?.error?.message || 'Impossible de charger les produits.',
                    'Erreur'
                );
                this.rapportProducts = [];
                this.isLoadingProducts = false;
            }
        });
    }

    retour() {
        this.router.navigate(['/rapports']);
    }

}
